import {
    createPagePagingActions,
    Field,
    FinalFormInput,
    SortDirection,
    StackSwitchApiContext,
    Table as AdminTable,
    TableFilterFinalForm,
    TableQuery,
    useTableQuery,
    useTableQueryFilter,
    useTableQueryPaging,
    useTableQuerySort,
    VisibleType,
} from "@comet/admin";
import { Grid, IconButton } from "@mui/material";
import { ContentWrapper, formatDateTimeAsString, FormattedDateWidthDateTime, useMemoizedTableVariables } from "@src/components";
import { StaticSelectField } from "@src/components/fields/StaticSelect";
import { ResetButton } from "@src/components/ResetButton";
import { StyledFilter, StyledLink, StyledSearch, TableFilter } from "@src/components/table/TableStyles";
import {
    GQLPackageType,
    type GQLPurchasesExportListAdminQuery,
    type GQLPurchasesExportListAdminQueryVariables,
    type GQLPurchasesListAdminQuery,
    type GQLPurchasesListAdminQueryVariables,
} from "@src/graphql.generated";
import ArrowRight from "@src/icons/ArrowRight";
import { type ExportVariables } from "@src/utils";
import { CSVExportButton } from "@src/utils/CSVExportButton";
import { useCSVExportPagedTableQuery } from "@src/utils/usePagedCSVExport";
import { type FunctionComponent, type PropsWithChildren, useCallback, useContext } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { purchasesExportListQuery, purchasesListQuery } from "./Table.gql";

interface FilterVariables {
    query?: string;
    packageType?: GQLPackageType;
}

interface TableProps {
    persistedStateId?: string;
}

const usePurchasesExport = () => {
    const size = 20;
    const intl = useIntl();
    const fileName = `${intl.formatMessage({
        id: "reviermanager.common.export.purchases",
        defaultMessage: "Lizenzverlängerungen",
    })}`;

    const { tableData, api } = useTableQuery<GQLPurchasesExportListAdminQuery, GQLPurchasesExportListAdminQueryVariables>()(
        purchasesExportListQuery,
        {
            resolveTableData: ({ licenceExportAdmin }) => ({
                data: licenceExportAdmin.nodes,
                totalCount: licenceExportAdmin.totalCount,
            }),
        },
    );

    return useCSVExportPagedTableQuery<ExportVariables>(
        api,
        {
            fromPage: 1,
            toPage: tableData?.totalCount ? Math.ceil(tableData.totalCount / size) : 1,
            variablesForPage: (page) => ({ page, size }),
        },
        fileName,
    );
};

const Table: FunctionComponent<PropsWithChildren<TableProps>> = ({ persistedStateId }) => {
    const pagingApi = useTableQueryPaging(1, { persistedStateId });
    const sortApi = useTableQuerySort({ columnName: "camera.cameraId", direction: SortDirection.DESC }, { persistedStateId });
    const stackApi = useContext(StackSwitchApiContext);
    const filterApi = useTableQueryFilter<FilterVariables>({}, { pagingApi, persistedStateId });
    const addAdditionalQueryVariablesFn = useCallback(
        () => ({ packageType: filterApi.current.packageType, query: filterApi.current.query }),
        [filterApi],
    );

    const intl = useIntl();

    const memoizedVariables = useMemoizedTableVariables<GQLPurchasesListAdminQueryVariables>({ pagingApi, sortApi, addAdditionalQueryVariablesFn });

    const { tableData, api, loading, error } = useTableQuery<GQLPurchasesListAdminQuery, GQLPurchasesListAdminQueryVariables>()(purchasesListQuery, {
        resolveTableData: ({ licencesAdmin }) => ({
            data: licencesAdmin.nodes,
            totalCount: licencesAdmin.totalCount,
            pagingInfo: createPagePagingActions(pagingApi, licencesAdmin),
        }),
        variables: {
            ...memoizedVariables,
            activated: true,
        },
    });

    const packageTypeOptions = [
        {
            label: intl.formatMessage({ id: "reviermanager.purchases.filter.packageType.basic", defaultMessage: "Basic" }),
            value: GQLPackageType.Basic,
        },
        {
            label: intl.formatMessage({ id: "reviermanager.purchases.filter.packageType.premium", defaultMessage: "Premium" }),
            value: GQLPackageType.Premium,
        },
    ];

    const handleFilterReset = () => {
        filterApi.formApi.reset({});
    };

    const exportApi = usePurchasesExport();

    return (
        <ContentWrapper marginVariant="table">
            <TableFilterFinalForm filterApi={filterApi}>
                <TableFilter container>
                    <Grid size={2}>
                        <StyledFilter>
                            <StaticSelectField
                                name="packageType"
                                label={intl.formatMessage({
                                    id: "reviermanager.purchases.filter.packageType",
                                    defaultMessage: "Paket-Typ",
                                })}
                                options={packageTypeOptions}
                                placeholder={intl.formatMessage({
                                    id: "reviermanager.filter.all",
                                    defaultMessage: "Alle",
                                })}
                                isClearable
                                variant="vertical"
                            />
                        </StyledFilter>
                    </Grid>
                    <Grid size={3}>
                        <StyledFilter>
                            <Field
                                type="text"
                                label={intl.formatMessage({ id: "reviermanager.devices.filter.fulltext", defaultMessage: "Suche" })}
                                name="query"
                                component={FinalFormInput}
                                placeholder={intl.formatMessage({
                                    id: "reviermanager.purchases.filter.fulltext.placeholder",
                                    defaultMessage: "Name, Code, ID, ...",
                                })}
                                startAdornment={<StyledSearch />}
                                variant="vertical"
                                fullWidth
                            />
                        </StyledFilter>
                    </Grid>
                    {Object.keys(filterApi.current).length > 0 && (
                        <Grid>
                            <ResetButton onClick={handleFilterReset} />
                        </Grid>
                    )}
                </TableFilter>
            </TableFilterFinalForm>
            <TableQuery api={api} loading={loading} error={error}>
                {tableData && (
                    <AdminTable
                        exportApis={[exportApi]}
                        {...tableData}
                        sortApi={sortApi}
                        rowName={intl.formatMessage(
                            {
                                id: "reviermanager.pages.purchases",
                                defaultMessage: "{count, plural, one {Lizenzverlängerung} other {Lizenzverlängerungen}}",
                            },
                            { count: tableData.totalCount },
                        )}
                        columns={[
                            {
                                name: "camera.cameraId",
                                header: intl.formatMessage({ id: "reviermanager.common.purchases.camera.cameraId", defaultMessage: "Gerät-ID" }),
                                sortable: true,
                                render: ({ camera }) => camera && <StyledLink to={`/devices/${camera.id}/edit`}>{camera.cameraId}</StyledLink>,
                            },
                            {
                                name: "package.name",
                                header: intl.formatMessage({ id: "reviermanager.common.purchases.package.name", defaultMessage: "Packetname" }),
                                sortable: true,
                                render: ({ package: { name } }) => name,
                            },
                            {
                                name: "licence.code",
                                header: intl.formatMessage({ id: "reviermanager.common.purchases.code", defaultMessage: "Code" }),
                                render: ({ code }) => code,
                            },
                            {
                                name: "camera.user.firstname",
                                header: intl.formatMessage({
                                    id: "reviermanager.common.purchases.camera.user.firstname",
                                    defaultMessage: "Vorname",
                                }),
                                render: ({ camera }) => camera?.user?.firstname,
                            },
                            {
                                name: "camera.user.lastname",
                                header: intl.formatMessage({
                                    id: "reviermanager.common.purchases.camera.user.lastname",
                                    defaultMessage: "Nachname",
                                }),
                                render: ({ camera }) => camera?.user?.lastname,
                            },
                            {
                                name: "licence.activatedAt",
                                header: intl.formatMessage({ id: "reviermanager.common.purchases.activatedAt", defaultMessage: "Kaufdatum" }),
                                render: ({ activatedAt }) => <FormattedDateWidthDateTime value={activatedAt} />,
                                sortable: true,
                                renderExcel: ({ activatedAt }) => formatDateTimeAsString(activatedAt, intl),
                            },
                            {
                                name: "activeFrom",
                                header: intl.formatMessage({
                                    id: "reviermanager.common.purchases.activeFrom",
                                    defaultMessage: "Lizenz aktiviert am",
                                }),
                                visible: { [VisibleType.Browser]: false, [VisibleType.Export]: true },
                                renderExcel: ({ activeFrom }) => formatDateTimeAsString(activeFrom, intl),
                            },
                            {
                                name: "activeTill",
                                header: intl.formatMessage({ id: "reviermanager.common.purchases.activeTill", defaultMessage: "Lizenz läuft ab am" }),
                                visible: { [VisibleType.Browser]: false, [VisibleType.Export]: true },
                                renderExcel: ({ activeTill }) => formatDateTimeAsString(activeTill, intl),
                            },
                            {
                                name: "actions",
                                visible: { [VisibleType.Browser]: true, [VisibleType.Export]: false },
                                render: ({ id }) => (
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            stackApi.activatePage("information", String(id));
                                        }}
                                    >
                                        <ArrowRight color="primary" />
                                    </IconButton>
                                ),
                            },
                        ]}
                    />
                )}
            </TableQuery>
            {tableData && (
                <CSVExportButton exportApi={exportApi}>
                    <FormattedMessage
                        id="cometAdmin.table.csvExportButton.licences"
                        defaultMessage="Alle Lizenzverlängerungen exportieren"
                        description="Export Button"
                    />
                </CSVExportButton>
            )}
        </ContentWrapper>
    );
};

export default Table;
